import type { Dir, InputFrame } from '../sim/index.ts';
import type { Circle, Layout } from '../render/layout.ts';
import { inCircle } from '../render/layout.ts';
import type { FireVariant } from '../settings.ts';
import { TapFireRecognizer } from './tapFire.ts';

export interface InputHooks {
  getLayout(): Layout;
  getFireVariant(): FireVariant;
  /** Player's on-screen centre in CSS px, or null when not playing. */
  getPlayerScreenPos(): [number, number] | null;
  isDpadEnabled(): boolean;
  onAccidentalFire(reason: string): void;
  onSwipe(dir: Dir): void;
  onTapStop(): void;
  onFirePress(): void;
  /** Any tap/click on the canvas (used for "tap to restart"). */
  onAnyTap(): void;
  /** Five charges banked: a press still held at 0.5 s fires the Beam as well as its shot (Systems Brief §2.1). */
  isBeamReady(): boolean;
  onBeamPress(): void;
}

/** Swipe must travel this many CSS px before it registers. */
const SWIPE_THRESHOLD_PX = 18;
/** A press shorter than this with no movement is a tap. */
const TAP_MAX_MS = 300;
/** Fire inputs within this window after a swipe gesture count as accidental. */
const ACCIDENTAL_FIRE_WINDOW_MS = 200;
/** Tap-on-player radius in cells. */
const TAP_STOP_RADIUS_CELLS = 1.2;
/** A fire press still held this long (unmoved) also fires the Beam when five charges are banked. */
const BEAM_HOLD_MS = 500;

interface Pointer {
  id: number;
  kind: 'swipe' | 'fire' | 'dpad';
  x0: number;
  y0: number;
  t0: number;
  swiped: boolean;
  dir?: Dir;
  beamTimer?: ReturnType<typeof setTimeout>;
  beamed?: boolean;
}

/**
 * Bible §3: the whole screen steers. Every pointerdown starts a swipe candidate, including over
 * the HUD and scenery; the only exception is the lantern hit target in variant A. Tap on Pip
 * stops. Variant B: a double-tap anywhere fires.
 */
export class InputController {
  private pendingDir: Dir = 0;
  private pendingStop = false;
  private pendingFire = false;
  private pendingBeam = false;
  private pendingSkip = false;
  private pointers = new Map<number, Pointer>();
  private lastSwipeAt = -Infinity;
  /** Variant B double-tap recogniser (300 ms / 32 px, decides on the second pointerdown). */
  private readonly tapFire = new TapFireRecognizer();
  /** performance.now() of the most recent pointerdown or keydown (restart-latency measurement). */
  lastInputAt = -Infinity;
  dpadHeld: Dir = 0;
  private readonly canvas: HTMLCanvasElement;
  private readonly hooks: InputHooks;

  constructor(canvas: HTMLCanvasElement, hooks: InputHooks) {
    this.canvas = canvas;
    this.hooks = hooks;
    canvas.addEventListener('pointerdown', this.onDown, { passive: false });
    canvas.addEventListener('pointermove', this.onMove, { passive: false });
    canvas.addEventListener('pointerup', this.onUp, { passive: false });
    canvas.addEventListener('pointercancel', this.onUp, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    // Mobile browsers run their own double-tap (zoom) and long-press gestures on touch events and
    // may swallow the second tap's pointer events. Claim the raw touch events so nothing is eaten.
    const block = (e: Event): void => e.preventDefault();
    canvas.addEventListener('touchstart', block, { passive: false });
    canvas.addEventListener('touchend', block, { passive: false });
    canvas.addEventListener('touchmove', block, { passive: false });
    canvas.addEventListener('dblclick', block);
    document.addEventListener('gesturestart', block, { passive: false });
    window.addEventListener('keydown', this.onKey);
  }

  /** Drain the input gathered since the last tick. */
  consumeFrame(): InputFrame {
    const f: InputFrame = { dir: this.pendingDir, stop: this.pendingStop, fire: this.pendingFire, skip: this.pendingSkip, beam: this.pendingBeam };
    this.pendingDir = 0;
    this.pendingStop = false;
    this.pendingFire = false;
    this.pendingBeam = false;
    this.pendingSkip = false;
    return f;
  }

  private queueDir(d: Dir): void {
    this.pendingDir = d;
    this.hooks.onSwipe(d);
  }

  private fire(reasonIfAccidental: string | null): void {
    if (reasonIfAccidental) this.hooks.onAccidentalFire(reasonIfAccidental);
    this.pendingFire = true;
    this.hooks.onFirePress();
  }

  private beam(): void {
    this.pendingBeam = true;
    this.hooks.onBeamPress();
  }

  /**
   * Beam detection on a fire press: the shot already went out on the press; if the same pointer is
   * still down and unmoved at BEAM_HOLD_MS and five charges are banked *now*, the Beam fires too
   * (so a Beam costs the held shot plus the charges). Checked at hold time, not at press time.
   */
  private armBeam(p: Pointer): void {
    p.beamTimer = setTimeout(() => {
      p.beamTimer = undefined;
      if (this.pointers.get(p.id) === p && !p.swiped && this.hooks.isBeamReady()) {
        p.beamed = true;
        this.beam();
      }
    }, BEAM_HOLD_MS);
  }

  private lanternHit(): Circle | null {
    if (this.hooks.getFireVariant() !== 'lantern') return null;
    return this.hooks.getLayout().lantern?.hit ?? null;
  }

  private dpadDir(x: number, y: number): Dir {
    const L = this.hooks.getLayout();
    if (!L.dpad || !this.hooks.isDpadEnabled()) return 0;
    const { cx, cy, size } = L.dpad;
    const b = size / 3;
    const dx = x - cx;
    const dy = y - cy;
    if (Math.abs(dx) > size / 2 || Math.abs(dy) > size / 2) return 0;
    if (Math.abs(dx) < b / 2 && Math.abs(dy) < b / 2) return 0;
    if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 2 : 4;
    return dy > 0 ? 3 : 1;
  }

  private onDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.canvas.setPointerCapture?.(e.pointerId);
    const x = e.clientX;
    const y = e.clientY;
    const now = performance.now();
    this.lastInputAt = now;
    this.pendingSkip = true;
    this.hooks.onAnyTap();
    const p: Pointer = { id: e.pointerId, kind: 'swipe', x0: x, y0: y, t0: now, swiped: false };
    const lantern = this.lanternHit();
    if (this.hooks.getFireVariant() === 'doubleTap') {
      // Variant B: fire on the second pointerdown of a double-tap. The press still steers if dragged.
      const firstTapAt = this.tapFire.down(e.pointerId, x, y, now);
      if (firstTapAt !== null) {
        // Fire on the press; the same press held still for 0.5 s also fires the Beam.
        this.fire(firstTapAt - this.lastSwipeAt < ACCIDENTAL_FIRE_WINDOW_MS ? 'double-tap right after a swipe' : null);
        this.armBeam(p);
      }
    } else {
      this.tapFire.reset();
    }
    if (lantern && inCircle(x, y, lantern.cx, lantern.cy, lantern.r)) {
      p.kind = 'fire';
      const swipeActive = [...this.pointers.values()].some((q) => q.kind === 'swipe');
      const reason = swipeActive ? 'lantern pressed during active swipe' : now - this.lastSwipeAt < ACCIDENTAL_FIRE_WINDOW_MS ? 'lantern pressed within 200ms of swipe' : null;
      // The shot goes out on the press (instant); the same press held 0.5 s adds the Beam.
      this.fire(reason);
      this.armBeam(p);
    } else {
      this.dpadOrSwipe(p, x, y);
    }
    this.pointers.set(e.pointerId, p);
  };

  private dpadOrSwipe(p: Pointer, x: number, y: number): void {
    const d = this.dpadDir(x, y);
    if (d !== 0) {
      p.kind = 'dpad';
      p.dir = d;
      this.dpadHeld = d;
      this.queueDir(d);
    }
  }

  private onMove = (e: PointerEvent): void => {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    e.preventDefault();
    this.tapFire.move(e.pointerId, e.clientX, e.clientY);
    const dx = e.clientX - p.x0;
    const dy = e.clientY - p.y0;
    if (p.kind === 'fire') {
      if (!p.swiped && Math.max(Math.abs(dx), Math.abs(dy)) >= SWIPE_THRESHOLD_PX) {
        p.swiped = true;
        if (p.beamTimer) clearTimeout(p.beamTimer);
        this.hooks.onAccidentalFire('lantern press that turned into a swipe');
        this.queueDir(this.dirOf(dx, dy)); // honour the intended swipe anyway
        p.x0 = e.clientX;
        p.y0 = e.clientY;
      }
      return;
    }
    if (p.kind === 'dpad') {
      const d = this.dpadDir(e.clientX, e.clientY);
      if (d !== 0 && d !== p.dir) {
        p.dir = d;
        this.dpadHeld = d;
        this.queueDir(d);
      }
      return;
    }
    if (Math.max(Math.abs(dx), Math.abs(dy)) >= SWIPE_THRESHOLD_PX) {
      p.swiped = true;
      if (p.beamTimer) clearTimeout(p.beamTimer);
      this.lastSwipeAt = performance.now();
      const d = this.dirOf(dx, dy);
      // A continued drag in the same direction is one intent, not a re-queue (keeps missed-turn telemetry honest).
      if (d !== p.dir) {
        p.dir = d;
        this.queueDir(d);
      }
      // Re-anchor so a continued drag can steer again without lifting.
      p.x0 = e.clientX;
      p.y0 = e.clientY;
    }
  };

  private onUp = (e: PointerEvent): void => {
    const now = performance.now();
    this.tapFire.up(e.pointerId, now);
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    this.pointers.delete(e.pointerId);
    if (p.beamTimer) clearTimeout(p.beamTimer);
    if (p.kind === 'dpad') this.dpadHeld = 0;
    if (p.kind === 'fire') return;
    if (p.kind !== 'swipe' || p.beamed) return;
    const dt = now - p.t0;
    if (p.swiped || dt > TAP_MAX_MS) return;
    // A tap on Pip = stop (both variants).
    const pos = this.hooks.getPlayerScreenPos();
    const L = this.hooks.getLayout();
    if (pos && inCircle(e.clientX, e.clientY, pos[0], pos[1], L.cell * TAP_STOP_RADIUS_CELLS)) {
      this.pendingStop = true;
      this.hooks.onTapStop();
    }
  };

  private dirOf(dx: number, dy: number): Dir {
    if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 2 : 4;
    return dy > 0 ? 3 : 1;
  }

  private onKey = (e: KeyboardEvent): void => {
    this.lastInputAt = performance.now();
    const map: Record<string, Dir> = { ArrowUp: 1, ArrowRight: 2, ArrowDown: 3, ArrowLeft: 4, w: 1, d: 2, s: 3, a: 4, W: 1, D: 2, S: 3, A: 4 };
    const d = map[e.key];
    if (d) {
      e.preventDefault();
      this.queueDir(d);
      return;
    }
    if (e.key === ' ' || e.key === 'Enter' || e.key === 'f') {
      e.preventDefault();
      this.fire(null);
      this.hooks.onAnyTap();
      return;
    }
    if (e.key === 'b' || e.key === 'B') {
      e.preventDefault();
      this.beam();
      return;
    }
    if (e.key === 'x' || e.key === 'Shift' || e.key === 'q') {
      e.preventDefault();
      this.pendingStop = true;
      this.hooks.onTapStop();
    }
  };
}
